import type { FunctionComponent } from 'react';
import styled from 'styled-components';
import Counter from '../atoms/Counter';
import ImageButton from './ImageButton.Container';
import AspectPoints from './AspectPoints';
import CurrentSeason from './CurrentSeason';
import { PositionFromProps } from '../../utils/styledcomponents';
import type { PositionProps } from '../../utils/styledcomponents';
import { EMPTY_TOTAL_SCORE } from '../../data/Score';
import type { TotalScore } from '../../data/Score';
import Assets from '../../assets/Assets';

const Bar = styled.div`
	align-items: center;
	background-color: ${(props) => props.theme.colors.white};
	border-bottom: ${(props) => props.theme.borderWidth.bar} solid
		${(props) => props.theme.colors.violet};
	border-left: ${(props) => props.theme.borderWidth.bar} solid
		${(props) => props.theme.colors.violet};
	border-bottom-left-radius: 7.5vmin;
	box-shadow: ${(props) => props.theme.shadow.bar};
	color: ${(props) => props.theme.colors.darkBlue};
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	font: ${(props) => props.theme.font.default};
	padding: 1vmin;
	will-change: transform;
	& > * {
		margin-left: 1.2vw;
		margin-right: 1.2vw;
	}
`;

const Section = styled.div`
	border-left: ${(props) => props.theme.borderWidth.bar} solid
		${(props) => props.theme.colors.violet};
	border-right: ${(props) => props.theme.borderWidth.bar} solid
		${(props) => props.theme.colors.violet};
	padding-left: 1.2vw;
	padding-right: 1.2vw;
`;

interface Props {
	className?: string;
	previousScore?: TotalScore;
	score: TotalScore;
	/**
	 * Animation delay in seconds
	 */
	animationDelay?: number;
	currentRoundIndex: number;
	roundCount: number;
	onSettingsClick: () => void;
}

const TotalScoreBar: FunctionComponent<Props> = (props: Props) => {
	const {
		className,
		previousScore = EMPTY_TOTAL_SCORE,
		score,
		animationDelay,
		currentRoundIndex,
		roundCount,
		onSettingsClick
	} = props;

	const getValue = (previousValue: number, currentValue: number) => {
		return (
			<Counter
				from={previousValue}
				to={currentValue}
				animationDelay={animationDelay}
			/>
		);
	};

	return (
		<Bar className={className}>
			<AspectPoints key="qualityOfLife" aspect="qualityOfLife">
				{getValue(previousScore.qualityOfLife, score.qualityOfLife)}
			</AspectPoints>
			<AspectPoints key="environment" aspect="environment">
				{getValue(previousScore.environment, score.environment)}
			</AspectPoints>
			<AspectPoints key="happiness" aspect="happiness">
				{getValue(previousScore.happiness, score.happiness)}
			</AspectPoints>
			<Section>
				<CurrentSeason
					currentRoundIndex={currentRoundIndex}
					roundCount={roundCount}
				/>
			</Section>
			<ImageButton
				src={Assets.images.settings}
				onClick={onSettingsClick}
				width="8vmin"
				height="8vmin"
			/>
		</Bar>
	);
};

export default styled(TotalScoreBar)<PositionProps>(PositionFromProps);
